import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, formatMoney, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} [priceDisplay]
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityNumber'];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = false;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('product:quantity-change', this.#handleProductQuantityChange, { signal });
    document.addEventListener('product:variant-change', this.#handleProductVariantChange, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  #setupIntersectionObserver() {
    const headerHeight = 200; // Fix simulator sticky header
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for CTA add-to-cart
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        const rect = entry.target.getBoundingClientRect();

        if (!entry.isIntersecting && rect.bottom < headerHeight) {
          this.#showStickyBar();
        } else {
          this.#hideStickyBar();
        }
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);

    // check after load page
    const rect = buyButtonsBlock.getBoundingClientRect();
    if (rect.bottom < headerHeight) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }

    // Observer footer
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );
    this.#mainBottomObserver.observe(footer);

    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton();
  }

  handleAddToCartClick = async () => {
    const targetButton = this.#resolveTargetAddToCartButton() || this.#targetAddToCartButton;
    if (!targetButton) return;
    targetButton.dataset.puppet = 'true';
    targetButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;
    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    this.updatedCallback();
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) this.dataset.currentVariantId = variant.id;
    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton();

    if (!variant) this.#handleVariantUnavailable();
    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = (_event) => {
    const btn = this.#resolveTargetAddToCartButton() || this.#targetAddToCartButton;
    if (btn) btn.dataset.puppet = 'false';
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #handleProductQuantityChange = (event) => {
    const { productId, quantity } = event.detail || {};
    if (productId != null && productId !== this.dataset.productId) return;
    if (quantity == null) return;
    this.#currentQuantity = Number(quantity) || 1;
    this.#updateButtonText();
  };

  #handleProductVariantChange = (event) => {
    const { productId, variantId, available } = event.detail || {};
    if (productId != null && productId !== this.dataset.productId) return;
    if (variantId !== undefined) this.dataset.currentVariantId = variantId != null ? String(variantId) : '';
    if (available !== undefined && this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = !available;
    }
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const candidates = sectionElement.querySelectorAll(
      `product-form-component[data-product-id="${productId}"]`
    );
    for (const el of candidates) {
      if (el.querySelector && el.querySelector('[ref="addToCartButton"]')) return el;
    }
    return candidates[0] || null;
  }

  #resolveTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;
    const btn = productForm.querySelector('[ref="addToCartButton"]');
    return btn && btn.tagName === 'BUTTON' ? btn : null;
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, priceDisplay, quantityNumber } = this.refs;
    if (!addToCartButton) return;
    const available = !addToCartButton.disabled;

    if (available) {
      if (quantityNumber) quantityNumber.innerHTML = 'x' + this.#currentQuantity;
      if (priceDisplay) {
        const priceTotal = priceDisplay.querySelector('.sticky-total');
        if (priceTotal) priceTotal.innerHTML = this.#formatMoney(Number(priceDisplay.dataset.price) * this.#currentQuantity);
      }
    }
  }

  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';
    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.moneyFormat.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return currency;
      let thousandsSeparator = ',', decimalSeparator = '.', precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      switch (placeholder) {
        case 'amount_no_decimals': precision = 0; break;
        case 'amount_with_comma_separator': thousandsSeparator = '.'; decimalSeparator = ','; break;
        case 'amount_no_decimals_with_comma_separator': thousandsSeparator = '.'; precision = 0; break;
        case 'amount_no_decimals_with_space_separator': thousandsSeparator = ' '; precision = 0; break;
        case 'amount_with_space_separator': thousandsSeparator = ' '; decimalSeparator = ','; break;
        case 'amount_with_period_and_space_separator': thousandsSeparator = ' '; decimalSeparator = '.'; break;
        case 'amount_with_apostrophe_separator': thousandsSeparator = "'"; decimalSeparator = '.'; break;
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);
    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}